"use client";

import { useEffect, useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";

/**
 * Lightweight website analytics client.
 * - Generates/retrieves session_id and visitor_id from localStorage
 * - Sends a pageview event on mount and on route changes
 * - Skips admin routes (/admin)
 */
export default function WebAnalyticsClient() {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const isAdmin = useMemo(() => (pathname || "").startsWith("/admin"), [pathname]);

	// Initialize IDs once
	useEffect(() => {
		if (typeof window === "undefined") return;
		try {
			if (!localStorage.getItem("sid")) localStorage.setItem("sid", crypto.randomUUID());
			if (!localStorage.getItem("vid")) localStorage.setItem("vid", crypto.randomUUID());
		} catch {}
	}, []);

	// Send pageview on first mount and on route changes
	useEffect(() => {
		if (typeof window === "undefined") return;
		if (isAdmin) return; // do not track admin

		const sid = localStorage.getItem("sid") || crypto.randomUUID();
		const vid = localStorage.getItem("vid") || sid;
		const domain = window.location.hostname;
		const qs = typeof searchParams?.toString === "function" ? searchParams.toString() : "";
		const path = qs ? `${pathname || "/"}?${qs}` : `${pathname || "/"}`;
		let referrer = document.referrer || "";
		try {
			const currentUrl = new URL(window.location.href);
			const utmSource = currentUrl.searchParams.get("utm_source");
			const sameOriginRef = referrer && referrer.startsWith(window.location.origin);
			if ((!referrer || sameOriginRef) && utmSource) {
				referrer = `utm:${utmSource}`;
			}
		} catch {}

		const payload = { session_id: sid, visitor_id: vid, domain, path, referrer };

		// Fire pageview
		fetch("/api/website/collect", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ ...payload, type: "pageview" }),
			keepalive: true,
		}).catch(() => {});
	}, [pathname, searchParams, isAdmin]);

	return null;
}
